import React, { useMemo } from 'react';
import { DatePicker, parseDate } from '@ark-ui/react/date-picker';
import type { DateValue } from '@ark-ui/react/date-picker';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface DateRangePickerInputProps extends BaseInputProps<string[]> {
  /** Minimální volitelné datum (ISO `YYYY-MM-DD`). */
  min?: string;
  /** Maximální volitelné datum (ISO `YYYY-MM-DD`). */
  max?: string;
  /** BCP-47 locale pro zobrazení data (RHF drží vždy ISO). */
  locale?: string;
  /** Počet zobrazených měsíců (default 2 — vhodné pro výběr rozsahu). */
  numOfMonths?: number;
  /** Lokalizovaný popisek tlačítka kalendáře. */
  triggerLabel?: string;
  className?: string;
}

const toDateValue = (iso: string | undefined | null) => {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
};

/**
 * Atomický výběr rozsahu dat (od–do) postavený na Ark UI `DatePicker`
 * v režimu `selectionMode="range"`.
 *
 * RHF drží hodnotu jako pole dvou ISO řetězců `[fromISO, toISO]`
 * (`YYYY-MM-DD`). Během výběru může mít pole délku 1 (vybrán jen začátek) —
 * `onBlur` (touched) se hlásí až po dokončení rozsahu. Popover zůstává otevřený
 * po výběru začátku a zavře se po výběru konce (Ark `closeOnSelect` default).
 */
export const DateRangePickerInput: React.FC<DateRangePickerInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  min,
  max,
  locale = 'cs-CZ',
  numOfMonths = 2,
  triggerLabel = 'Otevřít kalendář',
  className,
}) => {
  const dateValue = useMemo(
    () =>
      (value ?? [])
        .filter(Boolean)
        .map((iso) => toDateValue(iso))
        .filter((d): d is DateValue => !!d),
    [value],
  );

  return (
    <DatePicker.Root
      name={name}
      ids={{ input: (i) => (i === 0 ? id : `${id}-end`), label: () => labelId }}
      value={dateValue}
      min={toDateValue(min)}
      max={toDateValue(max)}
      locale={locale}
      selectionMode="range"
      numOfMonths={numOfMonths}
      disabled={disabled}
      readOnly={readOnly}
      onValueChange={(details) => {
        onChange(details.value.map((d) => d.toString()));
        // Touched až po dokončení rozsahu (obě data vybraná).
        if (details.value.length >= 2) onBlur();
      }}
      positioning={{ placement: 'bottom-start', gutter: 4 }}
      lazyMount
      unmountOnExit
      className={clsx(styles.colorRoot, className)}
    >
      <DatePicker.Control className={styles.dateRangeControl}>
        <DatePicker.Input
          ref={ref}
          index={0}
          placeholder={placeholder}
          onBlur={onBlur}
          className={styles.dateInput}
          aria-describedby={describedBy}
          aria-invalid={hasError || undefined}
          {...testAttributes}
        />
        <span className={styles.dateRangeSeparator} aria-hidden="true">
          –
        </span>
        <DatePicker.Input
          index={1}
          placeholder={placeholder}
          onBlur={onBlur}
          className={styles.dateInput}
          aria-invalid={hasError || undefined}
          data-testid={`${testAttributes['data-testid']}-end`}
        />
        <DatePicker.Trigger
          className={styles.dateTrigger}
          aria-label={triggerLabel}
        >
          <Icon name="calendar" size={18} />
        </DatePicker.Trigger>
      </DatePicker.Control>
      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content
            className={styles.dateContent}
            data-testid={`${testAttributes['data-testid']}-calendar`}
          >
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(api) => (
                  <>
                    <DatePicker.ViewControl className={styles.dateViewControl}>
                      <DatePicker.PrevTrigger className={styles.datePrevTrigger}>
                        <Icon name="chevron-left" size={16} />
                      </DatePicker.PrevTrigger>
                      <DatePicker.RangeText className={styles.dateViewTrigger} />
                      <DatePicker.NextTrigger className={styles.dateNextTrigger}>
                        <Icon name="chevron-right" size={16} />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <div className={styles.dateRangeMonths}>
                      {Array.from({ length: numOfMonths }, (_, offset) => {
                        const month = api.getOffset({ months: offset });
                        return (
                          <DatePicker.Table
                            key={offset}
                            className={styles.dateTable}
                          >
                            <DatePicker.TableHead>
                              <DatePicker.TableRow>
                                {api.weekDays.map((weekDay, index) => (
                                  <DatePicker.TableHeader
                                    key={index}
                                    className={styles.dateTableHeader}
                                  >
                                    {weekDay.narrow}
                                  </DatePicker.TableHeader>
                                ))}
                              </DatePicker.TableRow>
                            </DatePicker.TableHead>
                            <DatePicker.TableBody>
                              {month.weeks.map((week, weekIndex) => (
                                <DatePicker.TableRow key={weekIndex}>
                                  {week.map((day, dayIndex) => (
                                    <DatePicker.TableCell
                                      key={dayIndex}
                                      value={day}
                                      visibleRange={month.visibleRange}
                                    >
                                      <DatePicker.TableCellTrigger
                                        className={styles.dateCellTrigger}
                                      >
                                        {day.day}
                                      </DatePicker.TableCellTrigger>
                                    </DatePicker.TableCell>
                                  ))}
                                </DatePicker.TableRow>
                              ))}
                            </DatePicker.TableBody>
                          </DatePicker.Table>
                        );
                      })}
                    </div>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  );
};

DateRangePickerInput.displayName = 'DateRangePickerInput';
